import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '@/application/services';
import { Admin, LoginDto, HttpError } from '@/domain/entities';

interface AuthContextType {
  admin: Admin | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginDto) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = () => {
      const authenticated = authService.isAuthenticated();
      setIsLoading(false);
      // Note: In a real app, you might want to validate the token by fetching admin data
    };
    checkAuth();
  }, []);

  const login = async (credentials: LoginDto) => {
    try {
      const adminData = await authService.login(credentials);
      setAdmin(adminData);
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new Error('Login failed');
    }
  };

  const logout = () => {
    authService.logout();
    setAdmin(null);
  };

  const value: AuthContextType = {
    admin,
    isAuthenticated: !!admin || authService.isAuthenticated(),
    isLoading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

